/**
 * 온습도로거 배터리 · 신호 등급 배지.
 * 색만으로 구분하지 않는다 — 아이콘 모양(막대 수·채움 정도)과 등급 글자를 항상 함께 표시한다.
 *
 * 배터리(Li/SOCl2 3.6V): 이 전지는 수명 대부분을 3.6V 근처에서 평탄하게 유지하다가 끝에서 급히 떨어진다.
 *   그래서 전압이 조금만 내려가도 남은 수명이 짧다는 뜻이다 — 백분율로 바꿔 보여주지 않고 등급으로 보여준다.
 *   양호 ≥3.4 · 보통 3.3~3.4 · 주의 3.0~3.3(교체 준비) · 교체 <3.0 (백엔드 BATTERY_WARN_V 와 같은 경계)
 * 신호(dBm, CSQ 환산): 매우 좋음 ≥-85 · 좋음 -95~-85 · 보통 -105~-95 · 약함 <-105
 *   약함은 NB-IoT 가 반복 전송을 해 배터리 수명이 절반 이하로 준다(설정 가이드 배터리 탭) — 설치 위치 조정 대상.
 */
import React from 'react';
import { Tooltip } from 'antd';
import { gray } from '../theme/tokens';

export type Grade = { key: string; label: string; color: string; bg: string; level: number; desc: string };

export const BATTERY_GRADES: Grade[] = [
  { key: 'good', label: '양호', color: '#15803d', bg: '#dcfce7', level: 4, desc: '3.4V 이상 — 정상 (Li/SOCl2 평탄 구간)' },
  { key: 'ok',   label: '보통', color: '#0369a1', bg: '#e0f2fe', level: 3, desc: '3.3~3.4V — 정상 범위, 추세만 본다' },
  { key: 'warn', label: '주의', color: '#92400e', bg: '#fef3c7', level: 2, desc: '3.0~3.3V — 평탄 구간을 벗어남. 교체 배터리를 준비' },
  { key: 'bad',  label: '교체', color: '#b91c1c', bg: '#fee2e2', level: 1, desc: '3.0V 미만 — 곧 통신이 끊긴다. 즉시 교체' },
];
export const SIGNAL_GRADES: Grade[] = [
  { key: 'excellent', label: '매우 좋음', color: '#15803d', bg: '#dcfce7', level: 4, desc: '-85dBm 이상' },
  { key: 'good',      label: '좋음',     color: '#0369a1', bg: '#e0f2fe', level: 3, desc: '-95 ~ -85dBm' },
  { key: 'fair',      label: '보통',     color: '#92400e', bg: '#fef3c7', level: 2, desc: '-105 ~ -95dBm — 동작은 정상, 배터리 소모가 늘기 시작' },
  { key: 'weak',      label: '약함',     color: '#b91c1c', bg: '#fee2e2', level: 1, desc: '-105dBm 미만 — 반복 전송으로 배터리 수명 절반 이하. 위치 조정 또는 LTE-M SIM' },
];

export const batteryGrade = (v?: number | null): Grade | null =>
  v == null ? null : v >= 3.4 ? BATTERY_GRADES[0] : v >= 3.3 ? BATTERY_GRADES[1] : v >= 3.0 ? BATTERY_GRADES[2] : BATTERY_GRADES[3];
export const signalGrade = (dbm?: number | null): Grade | null =>
  dbm == null ? null : dbm >= -85 ? SIGNAL_GRADES[0] : dbm >= -95 ? SIGNAL_GRADES[1] : dbm >= -105 ? SIGNAL_GRADES[2] : SIGNAL_GRADES[3];

const Pill: React.FC<{ g: Grade; icon: React.ReactNode; value: string; tip: React.ReactNode; compact?: boolean }> = ({ g, icon, value, tip, compact }) => (
  <Tooltip title={tip}>
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: compact ? '1px 6px' : '2px 8px', borderRadius: 999,
      background: g.bg, color: g.color, fontSize: 12, fontWeight: 600, lineHeight: '18px', whiteSpace: 'nowrap',
      border: `1px solid ${g.color}22`,
    }}>
      {icon}
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      {!compact && <span style={{ fontWeight: 700 }}>{g.label}</span>}
    </span>
  </Tooltip>
);

/* 배터리 모양 — 채움 칸 수가 등급(4~1)을 형태로도 전달한다 */
const BatteryIcon: React.FC<{ level: number; color: string }> = ({ level, color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }} aria-hidden>
    <span style={{ display: 'inline-flex', gap: 1, padding: 1, border: `1.5px solid ${color}`, borderRadius: 3, height: 11 }}>
      {[1, 2, 3, 4].map(i => <span key={i} style={{ width: 3, height: '100%', borderRadius: 1, background: i <= level ? color : 'transparent' }} />)}
    </span>
    <span style={{ width: 2, height: 5, background: color, borderRadius: '0 1px 1px 0' }} />
  </span>
);

/* 신호 막대 — 높이가 다른 4개 막대 중 채운 수가 등급 */
const SignalIcon: React.FC<{ level: number; color: string }> = ({ level, color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 1.5, height: 12 }} aria-hidden>
    {[1, 2, 3, 4].map(i => (
      <span key={i} style={{ width: 3, height: 3 + i * 2.25, borderRadius: 1, background: i <= level ? color : `${color}33` }} />
    ))}
  </span>
);

export const BatteryBadge: React.FC<{ v?: number | null; compact?: boolean }> = ({ v, compact }) => {
  const g = batteryGrade(v);
  if (!g || v == null) return <span style={{ color: gray[400], fontSize: 12 }}>-</span>;
  return <Pill g={g} compact={compact} icon={<BatteryIcon level={g.level} color={g.color} />} value={`${v.toFixed(2)}V`}
    tip={<div><b>배터리 {g.label}</b> · {v.toFixed(2)}V<div style={{ fontSize: 12 }}>{g.desc}</div></div>} />;
};

export const SignalBadge: React.FC<{ dbm?: number | null; csq?: number | null; compact?: boolean }> = ({ dbm, csq, compact }) => {
  const g = signalGrade(dbm);
  if (!g || dbm == null) return <span style={{ color: gray[400], fontSize: 12 }}>-</span>;
  return <Pill g={g} compact={compact} icon={<SignalIcon level={g.level} color={g.color} />} value={`${dbm}dBm`}
    tip={<div><b>신호 {g.label}</b> · {dbm}dBm{csq != null ? ` (CSQ ${csq})` : ''}<div style={{ fontSize: 12 }}>{g.desc}</div></div>} />;
};

/* 표 위 범례 — 등급별 대수와 기준을 함께 보여준다 */
export const GradeLegend: React.FC<{ devices: any[] }> = ({ devices }) => {
  const count = (fn: (d: any) => Grade | null, key: string) => devices.filter(d => fn(d)?.key === key).length;
  const Item: React.FC<{ g: Grade; n: number; icon: React.ReactNode }> = ({ g, n, icon }) => (
    <Tooltip title={g.desc}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: gray[700], opacity: n ? 1 : 0.45 }}>
        {icon}<span>{g.label}</span><b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b>
      </span>
    </Tooltip>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', fontSize: 12, color: gray[500], marginBottom: 8 }}>
      <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
        <b style={{ color: gray[600] }}>배터리</b>
        {BATTERY_GRADES.map(g => <Item key={g.key} g={g} n={count(d => batteryGrade(d.last_battery_v), g.key)} icon={<BatteryIcon level={g.level} color={g.color} />} />)}
      </span>
      <span style={{ width: 1, height: 14, background: gray[200] }} />
      <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
        <b style={{ color: gray[600] }}>신호</b>
        {SIGNAL_GRADES.map(g => <Item key={g.key} g={g} n={count(d => signalGrade(d.last_signal_dbm), g.key)} icon={<SignalIcon level={g.level} color={g.color} />} />)}
      </span>
      <span style={{ width: 1, height: 14, background: gray[200] }} />
      <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
        <b style={{ color: gray[600] }}>최적화</b>
        {(['best', 'improve', 'action'] as const).map(k => {
          const og = OPT_GRADES[k];
          const n = devices.filter(d => evaluateOptimization(d).grade === k).length;
          return (
            <Tooltip key={k} title={og.desc}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: gray[700], opacity: n ? 1 : 0.45 }}>
                <span style={{ width: 13, height: 13, borderRadius: 7, background: og.color, color: '#fff', fontSize: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{og.icon}</span>
                <span>{og.label}</span><b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b>
              </span>
            </Tooltip>
          );
        })}
      </span>
    </div>
  );
};

/* ── 절전 · 데이터 최적화 판정 ──────────────────────────────
   설정 가이드 '절전 · 데이터 최적화' 카테고리의 기준을 장치마다 그대로 적용한다.
   월 데이터는 최근 30일 실측(관측 1일 이상)을 우선하고, 없으면 설정값으로 추정한다(실측 회귀식). */
export type OptCheck = { key: string; area: '절전' | '데이터' | '결손' | '신호' | '배터리' | '설정'; sev: 'ok' | 'info' | 'warn' | 'bad'; text: string };
export type OptResult = { grade: 'best' | 'improve' | 'action'; checks: OptCheck[]; monthKb: number | null; measured: boolean;
  recTdc: number; recN: number; configFixable: boolean };

export const OPT_GRADES = {
  best:    { label: '최적',     color: '#15803d', bg: '#dcfce7', icon: '✔', desc: '절전·데이터 기준을 모두 충족' },
  improve: { label: '개선 권장', color: '#92400e', bg: '#fef3c7', icon: '△', desc: '동작은 정상, 배터리·데이터를 더 아낄 수 있다' },
  action:  { label: '조치 필요', color: '#b91c1c', bg: '#fee2e2', icon: '!', desc: '데이터 초과·기록 누락·배터리 급소모 위험' },
} as const;

/* 요금제 데이터 한도 — 1NCE 10년 500MB(2026-11-04 확정, 국내 통신사 요금제 삭제)를 120개월로 균등하게 나눈 월 약 4.2MB.
   설정 가이드 '요금제' · '1NCE 적용 검토' 카테고리와 같은 값 — 한쪽만 바꾸지 말 것.
   carrier 인자는 호출부 호환을 위해 남겨 둔다(요금제가 하나라 값과 무관하다) */
export const dataQuota = (_carrier?: string | null): { kb: number; label: string } =>
  ({ kb: Math.round((500 * 1024) / 120), label: '1NCE 500MB/10년(월 균등)' });

/* 설정값 추정 — 권장 형식 HEX(AT+PRO=2,0): 헤더 53B + 항목 8B×N + IP/UDP 28B + ACK 39B. 실측(raw_len)이 1일 이상 쌓이면 실측이 우선한다 */
const estMonth = (tdc: number, n: number) => ((53 + 8 * n + 67) * (31 * 86400 / tdc)) / 1024;

export const evaluateOptimization = (d: any): OptResult => {
  const tdc = d.tdc_sec || 86400;
  const n = d.n_entries || 32;
  const tr = d.tr_min || 60;
  const tdcH = tdc / 3600;
  const cover = (n * tr) / 60;                                   // 업로드 1회가 싣는 시간 분량
  // 적용 기준(2026-11-04): 1일 1회 · N=32(상한). 간격이 8h 미만이면 1일 1회로 고치도록 안내하고 N 도 그 기준(32)으로 — 클릭 시 여는 값과 같게
  const recN = tdc < 28800 ? 32 : Math.min(32, Math.ceil((2 * tdcH * 60) / tr));
  const checks: OptCheck[] = [];
  let configFixable = false;

  if (tdc < 21600) { checks.push({ key: 'tdc', area: '절전', sev: 'bad', text: `업로드 ${tdcH}시간마다(하루 ${Math.round(24 / tdcH)}회) — 세션이 많아 배터리·데이터 소모가 크다 (적용 기준 1일 1회)` }); configFixable = true; }
  else if (tdc < 28800) { checks.push({ key: 'tdc', area: '절전', sev: 'warn', text: `업로드 ${tdcH}시간마다 — 세션이 늘어 배터리 소모가 커진다 (적용 기준 1일 1회 · 최소 8시간)` }); configFixable = true; }
  else if (tdc >= 86400) checks.push({ key: 'tdc', area: '절전', sev: 'ok', text: tdc === 86400 ? '업로드 1일 1회 — 적용 기준' : `업로드 ${tdcH}시간마다 — 1일 1회보다 길다(데이터가 하루 넘게 늦다)` });
  else checks.push({ key: 'tdc', area: '절전', sev: 'ok', text: `업로드 ${tdcH}시간마다 — 허용 범위 (적용 기준은 1일 1회, 같은 날 한낮 값을 퇴근 전에 봐야 하면 8시간)` });

  if (cover < tdcH) { checks.push({ key: 'n', area: '결손', sev: 'bad', text: `동봉 ${n}건 = ${cover}시간 분량 < 업로드 간격 ${tdcH}시간 — 사이의 측정값이 서버에 오지 않는다 (N=${recN})` }); configFixable = true; }
  else if (cover < 2 * tdcH && n >= 32) checks.push({ key: 'n', area: '결손', sev: 'ok', text: `동봉 ${n}건(상한) = 간격의 ${Math.round((cover / tdcH) * 10) / 10}배 — 유실 1회는 재전송(REDPT)이 복구` });
  else if (cover < 2 * tdcH && n < 32) { checks.push({ key: 'n', area: '결손', sev: 'warn', text: `동봉 ${n}건 = 간격의 ${Math.round((cover / tdcH) * 10) / 10}배 — 업로드 1회 유실 시 재전송에만 의존 (N=${recN})` }); configFixable = true; }
  else if (cover > 2.6 * tdcH && n > recN) { checks.push({ key: 'n', area: '데이터', sev: 'warn', text: `동봉 ${n}건 — 필요 이상(간격의 ${Math.round((cover / tdcH) * 10) / 10}배)이라 데이터 낭비 (N=${recN})` }); configFixable = true; }
  else if (tdc < 28800 && cover > 2.6 * tdcH) { checks.push({ key: 'n', area: '데이터', sev: 'warn', text: `동봉 ${n}건이 지금 간격(${tdcH}h)에는 과다 — 간격을 1일 1회로 바꾸면 N=${recN} 이 적정` }); configFixable = true; }
  else checks.push({ key: 'n', area: '결손', sev: 'ok', text: `동봉 ${n}건 = 간격의 ${Math.round((cover / tdcH) * 10) / 10}배 — 유실 1회 자가 복구` });

  const measured = d.usage?.month_kb_projected != null;
  const monthKb = measured ? d.usage.month_kb_projected : Math.round(estMonth(tdc, n) * 10) / 10;
  const src = measured ? `최근 ${d.usage.observed_days}일 실측 환산` : '설정값 추정';
  const q = dataQuota(d.sim_carrier);
  if (monthKb > q.kb) checks.push({ key: 'data', area: '데이터', sev: 'bad', text: `월 ${monthKb}KB (${src}) — ${q.label} 요금제 초과` });
  else if (monthKb > q.kb * 0.92) checks.push({ key: 'data', area: '데이터', sev: 'warn', text: `월 ${monthKb}KB (${src}) — ${q.label} 의 92% 초과, 여유 부족` });
  else checks.push({ key: 'data', area: '데이터', sev: 'ok', text: `월 ${monthKb}KB (${src}) — ${q.label} 안` });

  const sg = signalGrade(d.last_signal_dbm);
  if (sg?.key === 'weak') checks.push({ key: 'sig', area: '신호', sev: 'bad', text: `신호 ${d.last_signal_dbm}dBm 약함 — 반복 전송으로 배터리 수명 절반 이하. 본체 위치·높이 조정 (LTE-M 은 NB-IoT 보다 커버리지가 얕아 설치 위치가 더 중요하다)` });
  else if (sg?.key === 'fair') checks.push({ key: 'sig', area: '신호', sev: 'warn', text: `신호 ${d.last_signal_dbm}dBm 보통 — 배터리 소모가 늘기 시작. 설치 높이 조정 권장` });
  else if (sg) checks.push({ key: 'sig', area: '신호', sev: 'ok', text: `신호 ${d.last_signal_dbm}dBm ${sg.label}` });

  const bg = batteryGrade(d.last_battery_v);
  if (bg?.key === 'bad') checks.push({ key: 'bat', area: '배터리', sev: 'bad', text: `배터리 ${d.last_battery_v}V — 즉시 교체` });
  else if (bg?.key === 'warn') checks.push({ key: 'bat', area: '배터리', sev: 'warn', text: `배터리 ${d.last_battery_v}V — 교체 준비` });

  if (['pending', 'sent'].includes(d.cfg_status)) checks.push({ key: 'cfg', area: '설정', sev: 'info', text: '주기 변경이 기기로 전달되는 중 — 반영 후 다시 판정' });

  const grade = checks.some(c => c.sev === 'bad') ? 'action' : checks.some(c => c.sev === 'warn') ? 'improve' : 'best';
  return { grade, checks, monthKb, measured, recTdc: tdc < 28800 ? 86400 : tdc, recN, configFixable };
};

const SEV_MARK: Record<string, { mark: string; color: string }> = {
  ok: { mark: '✔', color: '#15803d' }, info: { mark: 'ℹ', color: '#0369a1' }, warn: { mark: '△', color: '#92400e' }, bad: { mark: '✖', color: '#b91c1c' },
};

export const OptimizationBadge: React.FC<{ d: any; onFix?: (recTdc: number, recN: number) => void }> = ({ d, onFix }) => {
  const r = evaluateOptimization(d);
  const g = OPT_GRADES[r.grade];
  const tip = (
    <div style={{ maxWidth: 360 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{g.label} — {g.desc}</div>
      {r.checks.map(c => (
        <div key={c.key} style={{ fontSize: 12, lineHeight: 1.6 }}>
          <span style={{ color: SEV_MARK[c.sev].color === '#15803d' ? '#86efac' : SEV_MARK[c.sev].color === '#b91c1c' ? '#fca5a5' : SEV_MARK[c.sev].color === '#92400e' ? '#fcd34d' : '#7dd3fc', fontWeight: 700 }}>{SEV_MARK[c.sev].mark}</span>{' '}
          <b>{c.area}</b> {c.text}
        </div>
      ))}
      {r.configFixable && onFix && <div style={{ fontSize: 12, marginTop: 4, color: '#c7d2fe' }}>클릭하면 권장값({Math.round(r.recTdc / 3600)}시간 · N {r.recN})으로 주기 변경 창을 연다</div>}
    </div>
  );
  return (
    <Tooltip title={tip}>
      <span onClick={r.configFixable && onFix ? () => onFix(r.recTdc, r.recN) : undefined}
        style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, cursor: r.configFixable && onFix ? 'pointer' : 'default' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '1px 8px', borderRadius: 999, background: g.bg, color: g.color,
          fontSize: 12, fontWeight: 700, lineHeight: '18px', border: `1px solid ${g.color}33`, width: 'fit-content' }}>
          <span style={{ width: 14, height: 14, borderRadius: 7, background: g.color, color: '#fff', fontSize: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{g.icon}</span>
          {g.label}
        </span>
        <span style={{ fontSize: 11, color: r.monthKb != null && r.monthKb > dataQuota(d.sim_carrier).kb ? '#b91c1c' : gray[500], fontVariantNumeric: 'tabular-nums' }}>
          월 {r.monthKb}KB · {r.measured ? '실측' : '추정'}
        </span>
      </span>
    </Tooltip>
  );
};
